import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import intl from 'components/i18n/ReactIntlWrapper';

interface LinkedSeat {
  id: number;
  amount: number;      // cents
  incoming: number;
  date: string;
  description: string;
  account?: { name: string; accountNumber: string };
  currency?: { code: string };
}

interface CandidateSeat {
  id: number;
  amount: number;      // cents
  date: string;
  description: string;
  account?: { name: string; accountNumber: string };
  currency?: { code: string };
}

interface Props {
  open: boolean;
  invoiceId: number;
  invoiceAmount: number;   // cents
  currency: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const InvoiceReconciliationDialog: React.FC<Props> = ({
  open,
  invoiceId,
  invoiceAmount,
  currency,
  onConfirm,
  onCancel,
}) => {
  const api: Api = useApi();
  const { success: toastSuccess, error: toastError } = useToastMessageQueue();

  const [linkedSeats, setLinkedSeats] = useState<LinkedSeat[]>([]);
  const [linkedLoading, setLinkedLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [candidates, setCandidates] = useState<CandidateSeat[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [addingId, setAddingId] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const formatCurrency = (amount: number, code = '') => {
    const dollars = amount / 100;
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: code || 'USD' }).format(dollars);
    } catch {
      return `${code} ${dollars.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    }
  };

  const loadLinkedSeats = useCallback(async () => {
    if (!invoiceId) return;
    setLinkedLoading(true);
    try {
      const res = await api.invoiceApiWrapper.getInvoiceLinkedSeats('1', invoiceId);
      setLinkedSeats(res.data?.data ?? []);
    } catch {
      setLinkedSeats([]);
    } finally {
      setLinkedLoading(false);
    }
  }, [api, invoiceId]);

  const searchCandidates = useCallback(async (term: string) => {
    if (!invoiceId) return;
    setSearchLoading(true);
    try {
      const res = await api.invoiceApiWrapper.searchInvoiceReconcileCandidates('1', invoiceId, term, 1, 20);
      setCandidates(res.data?.data?.queryResult ?? []);
    } catch {
      setCandidates([]);
    } finally {
      setSearchLoading(false);
    }
  }, [api, invoiceId]);

  useEffect(() => {
    if (open) {
      loadLinkedSeats();
      searchCandidates('');
    }
  }, [open, loadLinkedSeats, searchCandidates]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => searchCandidates(searchTerm), 350);
    return () => clearTimeout(timer);
  }, [searchTerm, searchCandidates, open]);

  const handleAdd = async (seatId: number) => {
    setAddingId(seatId);
    try {
      await api.invoiceApiWrapper.reconcileInvoice('1', invoiceId, [seatId]);
      toastSuccess({ message: intl.get('reconciliation.linked') || 'Credit linked successfully.' });
      await loadLinkedSeats();
      setCandidates((prev) => prev.filter((c) => c.id !== seatId));
    } catch {
      toastError({ message: intl.get('reconciliation.errorLinking') || 'Failed to link credit.' });
    } finally {
      setAddingId(null);
    }
  };

  const handleRemove = async (seatId: number) => {
    setRemovingId(seatId);
    try {
      await api.invoiceApiWrapper.unreconcileInvoiceFromSeat('1', invoiceId, seatId);
      toastSuccess({ message: intl.get('reconciliation.unlinked') || 'Credit unlinked.' });
      setLinkedSeats((prev) => prev.filter((s) => s.id !== seatId));
      // Refresh candidates so removed seat becomes available again
      await searchCandidates(searchTerm);
    } catch {
      toastError({ message: intl.get('reconciliation.errorUnlinking') || 'Failed to unlink credit.' });
    } finally {
      setRemovingId(null);
    }
  };

  const allocatedAmount = linkedSeats.reduce((sum, s) => sum + (s.amount ?? 0), 0);
  const invoiceAmountDecimal = invoiceAmount / 100;
  const allocatedAmountDecimal = allocatedAmount / 100;
  const remainingAmount = invoiceAmountDecimal - allocatedAmountDecimal;
  const allocationPct = invoiceAmountDecimal > 0
    ? Math.min(100, (allocatedAmountDecimal / invoiceAmountDecimal) * 100)
    : 0;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Icon icon="mdi:bank-check" width={22} />
          <Typography variant="h6">
            {intl.get('reconciliation.title') || 'Reconciliation'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Allocation summary */}
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2" color="text.secondary">
              {intl.get('reconciliation.allocated') || 'Allocated'}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatCurrency(allocatedAmount, currency)}
              {' / '}
              {formatCurrency(invoiceAmount, currency)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={allocationPct}
            color={allocationPct >= 100 ? 'success' : 'primary'}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Box display="flex" justifyContent="flex-end" mt={0.5}>
            <Typography
              variant="caption"
              color={remainingAmount < 0 ? 'error.main' : 'text.secondary'}
            >
              {intl.get('reconciliation.remaining') || 'Remaining:'}
              {' '}
              {formatCurrency(Math.abs(remainingAmount) * 100, currency)}
              {remainingAmount < 0 ? ` (${intl.get('reconciliation.over') || 'over'})` : ''}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Linked credits */}
        <Typography variant="subtitle2" display="flex" alignItems="center" gap={1} mb={1}>
          <Icon icon="mdi:link-variant" width={18} />
          {intl.get('reconciliation.linkedCredits') || 'Linked Credits'}
          <Chip label={linkedSeats.length} size="small" />
        </Typography>

        {linkedLoading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        ) : linkedSeats.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={1} mb={2}>
            {intl.get('reconciliation.noLinkedCredits') || 'No credits linked yet.'}
          </Typography>
        ) : (
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>{intl.get('ledger.account') || 'Account'}</TableCell>
                <TableCell>{intl.get('invoice.date') || 'Date'}</TableCell>
                <TableCell>{intl.get('ledger.description') || 'Description'}</TableCell>
                <TableCell align="right">{intl.get('invoice.amount') || 'Amount'}</TableCell>
                <TableCell width={48} />
              </TableRow>
            </TableHead>
            <TableBody>
              {linkedSeats.map((seat) => (
                <TableRow key={seat.id}>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {seat.account?.name ?? '—'}
                      {seat.account?.accountNumber ? ` (${seat.account.accountNumber})` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(seat.date).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>{seat.description}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontFamily="monospace" fontWeight={600} color="success.main">
                      {formatCurrency(seat.amount, seat.currency?.code || currency)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={intl.get('reconciliation.removeLink') || 'Remove link'}>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={removingId === seat.id}
                        onClick={() => handleRemove(seat.id)}
                      >
                        {removingId === seat.id
                          ? <CircularProgress size={16} />
                          : <Icon icon="mdi:link-variant-off" width={16} />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Search available credits */}
        <Typography variant="subtitle2" display="flex" alignItems="center" gap={1} mb={1}>
          <Icon icon="mdi:magnify" width={18} />
          {intl.get('reconciliation.availableCredits') || 'Available Credits'}
        </Typography>

        <TextField
          size="small"
          fullWidth
          placeholder={intl.get('reconciliation.creditSearchPlaceholder') || 'Search by account, description, or amount'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <Icon icon="mdi:magnify" width={18} style={{ marginRight: 8 }} />,
            endAdornment: searchLoading ? <CircularProgress size={16} /> : null,
          }}
          sx={{ mb: 2 }}
        />

        {!searchLoading && candidates.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={1}>
            {intl.get('reconciliation.noAvailableCredits') || 'No available credit entries found.'}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{intl.get('ledger.account') || 'Account'}</TableCell>
                <TableCell>{intl.get('invoice.date') || 'Date'}</TableCell>
                <TableCell>{intl.get('ledger.description') || 'Description'}</TableCell>
                <TableCell align="right">{intl.get('invoice.amount') || 'Amount'}</TableCell>
                <TableCell width={80} />
              </TableRow>
            </TableHead>
            <TableBody>
              {candidates.map((seat) => (
                <TableRow key={seat.id} hover>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {seat.account?.name ?? '—'}
                      {seat.account?.accountNumber ? ` (${seat.account.accountNumber})` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(seat.date).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>{seat.description}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontFamily="monospace" fontWeight={600} color="success.main">
                      {formatCurrency(seat.amount, seat.currency?.code || currency)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      disabled={addingId === seat.id}
                      startIcon={addingId === seat.id
                        ? <CircularProgress size={14} />
                        : <Icon icon="mdi:link-variant-plus" width={14} />}
                      onClick={() => handleAdd(seat.id)}
                    >
                      {intl.get('reconciliation.link') || 'Link'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onCancel}>
          {intl.get('workflow.action.cancel') || 'Cancel'}
        </Button>
        <Button variant="contained" color="primary" onClick={onConfirm}>
          {intl.get('reconciliation.confirmAndProceed') || 'Confirm & Proceed'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceReconciliationDialog;
